import React, { useEffect, useState, useRef, useContext } from "react";
import { Box, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { SocketContext } from "../../context/socket";
import { getUserId } from "../../constants/function";

import ApiService from "./../../services/api-service";
import notifier from "./../../components/Notifier";
import SIKIload from "../../components/SIKIload/SIKIload";
import FeatureBox from "../../components/FeatureBox";
import VideoCarousel from "../../components/Homev2/VideoCarousel";
import TopBan from "../../components/Homev2/topban";
import BannerSection from "../../components/Homev2/BannerSection";
import FeaturedSection from "../../components/Homev2/FeaturedSection";
import FeaturedArtists from "../../components/Homev2/FeaturedArtists";
import SidebarRight from "../../components/Homev2/SidebarRight";
import LeftBar from "../../components/LeftBar";

import { startStream } from "../../action/stream";
import { useAgoraRTC } from "../../services/useAgoraRTC";

export default function HomePage({ studioId, onVideoSelect, isOwner, ...props }) {
  const socketContext = useContext(SocketContext);
  const socket = socketContext?.Socket;
  const { t } = useTranslation("common");
  const dispatch = useDispatch();

  const [liveStream, setLiveStream] = useState([]);
  const streamRef = useRef([]);
  const [bannerData, setBannerData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Socket context exists but socket might not be initialized yet
  // This is normal on initial load/refresh
  const currentUserId = getUserId();
  const [agoraInfo, setAgoraInfo] = useState({
    token: null,
    channelName: null,
    appId: null,
    uid: null,
    role: "audience",
    enableVideo: false,
    joined: false,
  });

  const {
    localVideoTrack,
    remoteUsers,
    joinChannel,
    leaveChannel,
  } = useAgoraRTC({
    appId: agoraInfo.appId,
    channel: agoraInfo.channelName,
    token: agoraInfo.token,
    uid: agoraInfo.uid,
    role: agoraInfo.role,
    enableAudio: true,
    enableVideo: agoraInfo.enableVideo,
    onJoinSuccess: () => {
      console.log('Agora join success');
    },
  });

  useEffect(() => {
    if (!agoraInfo.joined && agoraInfo.token && agoraInfo.channelName) {
      joinChannel();
      setAgoraInfo((prev) => ({ ...prev, joined: true }));
    }
  }, [agoraInfo, joinChannel]);

  useEffect(() => {
    const loadData = async () => {
      console.log('loadData called');
      setLoading(true);
      try {
        const [liveStreamData, banner] = await Promise.all([
          fetchLiveStreams(),
          fetchBannerData(),
        ]);
        console.log('Data loaded:', { liveStreamData, banner });
        setLiveStream(liveStreamData);
        streamRef.current = liveStreamData;
        setBannerData(banner);
        
        // Check if user has an active live stream and auto-rejoin as host
        await checkAndRejoinHostStream(liveStreamData);
      } catch (error) {
        console.error('Error in loadData:', error);
        notifier.error("Failed to load data.");
      } finally {
        console.log('Setting loading to false in loadData');
        setLoading(false);
      }
    };

    loadData();

    if (socket) {
      socket.on("stream-joined", handleStreamJoined);
      socket.on("stream-ended", handleStreamEnded);
    }

    // Refresh live streams every 30 seconds to catch any missed socket events
    const refreshInterval = setInterval(async () => {
      try {
        const freshLiveStreams = await fetchLiveStreams();
        setLiveStream(freshLiveStreams);
        streamRef.current = freshLiveStreams;
      } catch (error) {
        console.error("Error refreshing live streams:", error);
      }
    }, 30000);

    return () => {
      if (socket) {
        socket.off("stream-joined", handleStreamJoined);
        socket.off("stream-ended", handleStreamEnded);
      }
      clearInterval(refreshInterval);
    };
    // eslint-disable-next-line
  }, [socket]);

  const fetchLiveStreams = async () => {
    try {
      const response = await ApiService.LIVESTREAM();
      return response.result || [];
    } catch (error) {
      notifier.error("Error fetching live streams.");
      return [];
    }
  };

  const fetchBannerData = async () => {
    try {
      const [songResponse, playlistResponse] = await Promise.all([
        ApiService.MUSIC_MOST_STREAM_WEEK(),
        ApiService.PLAYLIST_MOST_STREAMED(),
      ]);
      return {
        mostStreamedSong: songResponse.result?.[0],
        mostStreamedPlaylist: playlistResponse.result?.[0],
      };
    } catch (error) {
      return {};
    }
  };

  const handleStreamJoined = (streamData) => {
    const newStream = streamData?.livestreamData;
    if (newStream && newStream.channelName) {
      if (!streamRef.current.some((s) => s.channelName === newStream.channelName)) {
        const updated = [newStream, ...streamRef.current];
        setLiveStream(updated);
        streamRef.current = updated;
      }
    }
  };

  const handleStreamEnded = ({ channelName }) => {
    const updated = streamRef.current.filter((s) => s.channelName !== channelName);
    setLiveStream(updated);
    streamRef.current = updated;
  };

  const checkAndRejoinHostStream = async (liveStreamData) => {
    if (!currentUserId || !liveStreamData.length) return;
    
    console.log('Checking for host stream auto-rejoin:', {
      currentUserId,
      liveStreamCount: liveStreamData.length,
      fullStreams: liveStreamData
    });
    
    // Log each stream in detail
    liveStreamData.forEach((stream, index) => {
      console.log(`Stream ${index}:`, {
        channelName: stream.channelName,
        createdBy: stream.createdBy,
        owner: stream.owner,
        title: stream.title,
        _id: stream._id,
        fullObject: stream
      });
    });
    
    // Find if user has an active live stream where they are the host
    // Check multiple possible property names for the creator/owner
    const userHostStream = liveStreamData.find(stream => {
      const createdBy = stream.createdBy?.toString() || stream.createdBy;
      const owner = stream.owner?.toString() || stream.owner;
      const createdByDetailsId = stream.createdByDetails?._id?.toString();
      const currentUserStr = currentUserId.toString();
      
      console.log('Comparing IDs for stream:', {
        streamTitle: stream.title,
        createdBy,
        owner,
        createdByDetailsId,
        currentUserStr,
        matches: {
          createdByMatch: createdBy === currentUserStr,
          ownerMatch: owner === currentUserStr,
          createdByDetailsMatch: createdByDetailsId === currentUserStr
        }
      });
      
      return createdBy === currentUserStr || 
             owner === currentUserStr || 
             createdByDetailsId === currentUserStr;
    });
    
    if (userHostStream) {
      console.log('Found user host stream, auto-rejoining:', userHostStream.channelName);
      notifier.info('Reconnecting to your live stream...');
      
      try {
        // Auto-rejoin the stream as host
        await handleClick(userHostStream.channelName);
      } catch (error) {
        console.error('Error auto-rejoining host stream:', error);
        notifier.error('Failed to reconnect to your live stream. Click on it to rejoin manually.');
      }
    } else {
      console.log('No host stream found for current user');
    }
  };

  const handleClick = async (channelName) => {
    try {
      setLoading(true);
      const joinResp = await ApiService.LIVEJOIN({
        channelName,
        platform: "web",
      });

      if (
        joinResp?.status === "success" &&
        joinResp.result?.token &&
        joinResp.result?.channelName &&
        joinResp.result?.appId
      ) {
        const detailResp = await ApiService.PLAYLIST_DETAIL({
          _id: channelName,
          pageNo: 1,
          pageSize: 1,
        });

        let role = "audience";
        let enableVideo = false;
        
        // Check if current user is the host using multiple possible properties
        const streamData = detailResp?.result;
        const isHost = streamData && currentUserId && (
          (streamData.createdBy && streamData.createdBy.toString() === currentUserId.toString()) ||
          (streamData.owner && streamData.owner.toString() === currentUserId.toString()) ||
          (streamData.createdByDetails?._id && streamData.createdByDetails._id.toString() === currentUserId.toString())
        );
        
        console.log('Host check in handleClick:', {
          currentUserId,
          streamCreatedBy: streamData?.createdBy,
          streamOwner: streamData?.owner,
          streamCreatedByDetailsId: streamData?.createdByDetails?._id,
          isHost
        });
        
        if (isHost) {
          role = "host";
          enableVideo = true;
        }

        dispatch(startStream({ streamData: detailResp.result }));

        console.log('Setting Agora info with role:', {
          role,
          enableVideo,
          isHost,
          token: joinResp.result.token ? 'present' : 'missing'
        });

        // Navigate to streaming page - let it generate fresh token from backend
        window.location.href = `/app/LiveVideoStreaming/${channelName}?role=${role}&enableVideo=${enableVideo}`;
      } else {
        notifier.error("LIVEJOIN failed. Missing token or channel info.");
        setLoading(false);
      }
    } catch (error) {
      notifier.error("Error joining live stream.");
      setLoading(false);
    }
  };

return (
  <Box sx={{ minHeight: "100vh", backgroundColor: "black", position: 'relative', zIndex: 9999 }}>
    {/* Right Sidebar (fixed) */}
    <Box
      sx={{
        position: "fixed",
        top: 64,
        right: 0,
        width: "320px",
        height: "calc(100vh - 64px)",
        zIndex: 1,
        borderLeft: "1px solid rgba(255,255,255,0.1)",
        display: { xs: "none", md: "flex" },
        flexDirection: "column",
        overflowY: "auto",
        overflowX: "hidden",
        "&::-webkit-scrollbar": {
          width: "6px",
        },
        "&::-webkit-scrollbar-track": {
          background: "rgba(255, 255, 255, 0.05)",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "rgba(255, 255, 255, 0.2)",
          borderRadius: "3px",
          "&:hover": {
            background: "rgba(255, 255, 255, 0.3)",
          },
        },
      }}
    >
      <SidebarRight />
    </Box>

    {/* Main Content */}
    <Box
      sx={{
        ml: { md: "0px" }, // Keep the margin since LeftBar is rendered elsewhere
        mr: { md: "320px" },
        pt: 0,
        backgroundColor: "black",
      }}
    >
      <Box sx={{ maxWidth: "1200px", mx: "auto", px: 2, pb: 2 }}>
        <TopBan />
        {liveStream.length > 0 && (
        <Box sx={{ marginBottom: 3, pl: 3 }}> {/* Add padding here instead */}
            <FeatureBox
              isStreaming={true}
              title={t("home.tabs.featured.header5", { defaultValue: "Now Live" })}
              route={"/app/feature/NowLive"}
              music={liveStream}
              hideIcons={true}
              isPlaylistNft={true}
              onStream={handleClick}
              sx={{ paddingLeft: 50 }} // Add this to move content to the right


            />
          </Box>
        )}
        <VideoCarousel studioId={studioId} onVideoSelect={onVideoSelect} isOwner={isOwner} />
        <FeaturedSection />
        <FeaturedArtists />
        <BannerSection bannerData={bannerData} />
      </Box>
    </Box>

    {/* Loading Overlay */}
    {loading && (
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100vh",
          backgroundColor: "rgba(0, 0, 0, 0.9)",
          zIndex: 2000,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <SIKIload />
      </Box>
    )}
  </Box>
);
}